import React, { useEffect, useState } from 'react'
import moment from 'moment'
import parse from 'html-react-parser'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import BigModal from '../../../components/Modals/BigModal'
import AddNewTask from '../../userPages/tasks/AddNewTask'
import {
  InfoList,
  SectionHeader,
} from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import { taskStatusColor } from './safetyAlertHelpers'
// MUI
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddTaskIcon from '@mui/icons-material/AddTask'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'

const Section = ({ title, caption, action, children }) => (
  <Stack spacing={1.25}>
    <SectionHeader title={title} caption={caption} action={action} />
    {children}
  </Stack>
)

const Card = ({ children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      borderRadius: 2,
      border: (t) => `1px solid ${t.palette.divider}`,
      ...sx,
    }}
  >
    {children}
  </Paper>
)

const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ')

// Right-hand drawer for a single safety alert. `alertId` drives the fetch;
// `canAction` hides accept/dismiss/edit for users outside Governance who reach
// the alert through a linked task. onChanged(alert, change) lets the list
// update without a reload - change is 'accepted' | 'updated' | 'dismissed'.
const SafetyAlertDrawer = ({ alertId, onClose, onChanged, onDismiss, canAction }) => {
  const [alert, setAlert] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [mode, setMode] = useState(null) // null | 'accept' | 'edit'
  const [actionTaken, setActionTaken] = useState('')
  const [addAnnouncement, setAddAnnouncement] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [openTask, setOpenTask] = useState(false)
  const navigate = useNavigate()

  const load = () => {
    if (!alertId) return
    setLoading(true)
    setLoadError(null)
    API.get(`/safety-alert/${alertId}`)
      .then((res) => {
        if (res.status === 200 && res.data) {
          setAlert(res.data)
        } else {
          setLoadError(
            res.status === 404
              ? 'This alert no longer exists — it may have been dismissed.'
              : 'Unable to load this alert.',
          )
        }
      })
      .catch(() => setLoadError('Unable to load this alert.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    setAlert(null)
    setMode(null)
    setActionTaken('')
    setAddAnnouncement(false)
    setSaveError(null)
    load()
  }, [alertId])

  const isPending = alert && alert.accepted !== true
  const dirty = mode && actionTaken.trim() !== (alert?.actionTaken ?? '')

  const handleClose = () => {
    if (dirty && !window.confirm('Discard the action you have typed?')) return
    onClose()
  }

  const save = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!actionTaken.trim()) return
    setSaving(true)
    setSaveError(null)
    const wasPending = isPending
    API.put('/safety-alerts', {
      alertID: alert._id,
      accepted: true,
      actionTaken: actionTaken.trim(),
      announcement: addAnnouncement,
    })
      .then((res) => {
        if (res.status === 200) {
          setAlert(res.data)
          setMode(null)
          setAddAnnouncement(false)
          onChanged?.(res.data, wasPending ? 'accepted' : 'updated')
        } else {
          setSaveError(res.data?.error ?? 'Unable to save this alert.')
        }
      })
      .catch(() => setSaveError('Unable to save this alert.'))
      .finally(() => setSaving(false))
  }

  const startEdit = (next) => {
    setActionTaken(alert?.actionTaken ?? '')
    setAddAnnouncement(false)
    setSaveError(null)
    setMode(next)
  }

  const tasks = alert?.tasks ?? []
  const openTasks = tasks.filter((t) => t.taskStatus !== 'Completed').length

  const actionForm = (
    <Card>
      <Stack component="form" onSubmit={save} spacing={1.5}>
        <TextField
          autoFocus
          value={actionTaken}
          onChange={(e) => setActionTaken(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          label="Action taken"
          placeholder="e.g. Checked stock at all bases — affected batch not held."
          required
        />
        {!alert?.announcement && (
          <FormControlLabel
            control={
              <Checkbox
                checked={addAnnouncement}
                onChange={(e) => setAddAnnouncement(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Publish as an announcement
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Staff must acknowledge it; links to the MHRA notice.
                </Typography>
              </Box>
            }
          />
        )}
        {saveError && <Alert severity="error">{saveError}</Alert>}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={() => setMode(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={saving || !actionTaken.trim()}
          >
            {mode === 'accept' ? 'Accept alert' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>
    </Card>
  )

  return (
    <>
      <Drawer
        open={!!alertId}
        anchor="right"
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 500, lg: 580 },
            maxWidth: '100vw',
            bgcolor: 'background.default',
          },
        }}
      >
        <Stack height="100%">
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(237, 108, 2, 0.12)',
                  color: 'warning.main',
                  flexShrink: 0,
                }}
              >
                <WarningAmberIcon />
              </Box>
              <Box minWidth={0} flex={1}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                    Safety Alert
                  </Typography>
                  {alert && (
                    <Chip
                      size="small"
                      label={isPending ? 'Awaiting review' : 'Accepted'}
                      color={isPending ? 'warning' : 'success'}
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                </Stack>
                {alert && (
                  <Typography variant="caption" color="text.secondary">
                    {alert.source} · published{' '}
                    {moment(alert.updated).format('Do MMM YYYY')}
                  </Typography>
                )}
              </Box>
              <IconButton onClick={handleClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>
          {loading && <LinearProgress />}

          <Stack spacing={3} sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            {loadError && <Alert severity="warning">{loadError}</Alert>}

            {alert && (
              <>
                {/* Alert content */}
                <Stack spacing={1.25}>
                  <Typography variant="subtitle1" fontWeight={700} lineHeight={1.35}>
                    {parse(alert.title ?? '')}
                  </Typography>
                  {alert.summary && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {alert.summary}
                    </Typography>
                  )}
                  {alert.link && (
                    <Box>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        onClick={() =>
                          window.open(alert.link, '_blank', 'noopener')
                        }
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        Read the full alert on GOV.UK
                      </Button>
                    </Box>
                  )}
                </Stack>

                {/* Decision */}
                {isPending && (
                  <Section
                    title="Your review"
                    caption="Does this alert affect your service?"
                  >
                    {!canAction ? (
                      <Alert severity="info">
                        Only the Governance team can review safety alerts.
                      </Alert>
                    ) : mode === 'accept' ? (
                      actionForm
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleOutlineIcon />}
                          onClick={() => startEdit('accept')}
                          sx={{ borderRadius: 2, textTransform: 'none', py: 1 }}
                        >
                          Relevant — record action
                        </Button>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          startIcon={<DoNotDisturbAltIcon />}
                          onClick={() => onDismiss?.(alert)}
                          sx={{ borderRadius: 2, textTransform: 'none', py: 1 }}
                        >
                          Not relevant — dismiss
                        </Button>
                      </Stack>
                    )}
                  </Section>
                )}

                {!isPending && (
                  <Section
                    title="Review"
                    action={
                      canAction &&
                      mode !== 'edit' && (
                        <Button
                          size="small"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => startEdit('edit')}
                          sx={{ textTransform: 'none' }}
                        >
                          Edit
                        </Button>
                      )
                    }
                  >
                    {mode === 'edit' ? (
                      actionForm
                    ) : (
                      <Card>
                        <Typography
                          component="div"
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
                        >
                          Action taken
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                          {alert.actionTaken || '—'}
                        </Typography>
                      </Card>
                    )}
                    <InfoList
                      rows={[
                        {
                          label: 'Accepted by',
                          value: alert.actionedBy ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar
                                src={alert.actionedBy.photograph}
                                sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
                              >
                                {alert.actionedBy.firstName?.[0]}
                              </Avatar>
                              <Typography variant="body2" fontWeight={600}>
                                {fullName(alert.actionedBy)}
                              </Typography>
                            </Stack>
                          ) : null,
                        },
                        {
                          label: 'Accepted on',
                          value: alert.actionedAt
                            ? moment(alert.actionedAt).format('Do MMM YYYY [at] HH:mm')
                            : null,
                        },
                        {
                          label: 'Announcement',
                          value: alert.announcement ? (
                            <Button
                              size="small"
                              startIcon={<CampaignOutlinedIcon />}
                              onClick={() => navigate(`/announcements/${alert.announcement}`)}
                              sx={{ textTransform: 'none' }}
                            >
                              View announcement
                            </Button>
                          ) : (
                            'None'
                          ),
                        },
                      ]}
                    />
                  </Section>
                )}

                {/* Tasks */}
                {(!isPending || tasks.length > 0) && (
                  <Section
                    title="Follow-up tasks"
                    caption={
                      tasks.length
                        ? `${openTasks} open of ${tasks.length}`
                        : 'Assign follow-up work to a person or team'
                    }
                    action={
                      canAction && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddTaskIcon />}
                          onClick={() => setOpenTask(true)}
                          sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                          Add task
                        </Button>
                      )
                    }
                  >
                    {tasks.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No tasks yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {tasks.map((task) => (
                          <Card key={task._id} sx={{ p: 1.25 }}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              {task.assignedToUser ? (
                                <Tooltip title={fullName(task.assignedToUser)}>
                                  <Avatar
                                    src={task.assignedToUser.photograph}
                                    sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                                  >
                                    {task.assignedToUser.firstName?.[0]}
                                    {task.assignedToUser.lastName?.[0]}
                                  </Avatar>
                                </Tooltip>
                              ) : null}
                              <Box minWidth={0} flex={1}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {task.taskName || 'Task'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                  {task.assignedToUser
                                    ? fullName(task.assignedToUser)
                                    : task.assignedToTeam
                                    ? `${task.assignedToTeam} team`
                                    : 'Unassigned'}
                                  {task.dueDate &&
                                    ` · due ${moment(task.dueDate).format('DD MMM YYYY')}`}
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                label={task.taskStatus ?? 'New'}
                                color={taskStatusColor(task.taskStatus)}
                                sx={{ borderRadius: 1, fontWeight: 600 }}
                              />
                            </Stack>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Section>
                )}
              </>
            )}
          </Stack>
        </Stack>
      </Drawer>
      {/* Sibling of the Drawer so the task form's submit can't bubble into ours */}
      <BigModal open={openTask} onClose={() => setOpenTask(false)}>
        <AddNewTask
          passedSafetyAlert={alert?._id}
          successAction={() => {
            setOpenTask(false)
            load()
            onChanged?.(alert, 'updated')
          }}
        />
      </BigModal>
    </>
  )
}

export default SafetyAlertDrawer
